import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  MONTH_NAMES,
  WEEKDAY_NAMES,
  addMonths,
  currentMonth,
  formatDayLong,
  formatMonthLong,
  isSameDay,
  monthGrid,
  parseDateTimeValue,
  parseDayKey,
  parseMonthKey,
  toDateTimeValue,
  toDayKey,
  toMonthKey,
  todayParts,
  type CalendarCell,
  type CivilDate,
  type CivilMonth,
} from '@/lib/calendar';
import { cn } from '@/lib/utils';
import { strings } from '@/i18n/strings';

export type DatePickerMode = 'date' | 'month' | 'datetime';

export interface DatePickerProps {
  mode?: DatePickerMode;
  /** `YYYY-MM-DD`, `YYYY-MM` yoki `YYYY-MM-DDTHH:mm` — rejimga qarab. Bo'sh satr — tanlanmagan. */
  value: string;
  onChange: (value: string) => void;
  /**
   * Tugmaning ekran o'quvchisidagi nomi.
   *
   * ATAYLAB majburiy: nazorat `<button>`, va `<label>` bilan o'ralgan tugma o'z nomini o'ram
   * matnidan OLMAYDI — ya'ni buni tashlab ketish "tugma" deb o'qiladigan maydon qoldirardi.
   */
  ariaLabel: string;
  /** Forma `FormData` bilan yuborilsa qiymat shu nom ostida ketadi. */
  name?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Sana tanlash — TOSHKENT vaqtida tug'iladi.
 *
 * Brauzerning o'z `<input type="date">` i ikkita muammoni olib keladi va ikkalasi ham shu
 * mahsulotda qimmat:
 *
 *  1. "Bugun" belgisi operator noutbukining zonasidan olinadi. `lib/time.ts` dagi izoh aynan shu
 *     haqda: uzoqdan ulangan yoki soati noto'g'ri qo'yilgan mashinada efir sanasi bir kun narida
 *     belgilanadi, va xato faqat efir kuni ko'rinadi.
 *  2. Panel OS lokalida chiziladi — inglizcha oy nomlari va yakshanbadan boshlanadigan hafta.
 *
 * Shuning uchun kalendar to'liq shu yerda chiziladi: "bugun" `@qaytim/shared` ning Toshkent
 * helperlaridan keladi, hafta dushanbadan boshlanadi, oy nomlari o'zbekcha.
 *
 * SOAT esa native `<input type="time">` da qoladi (`datetime` rejimi). Bu izchil emasday
 * ko'rinadi, lekin sabab bor: yuqoridagi ikkala muammo ham SANAGA tegishli — soat-daqiqada
 * na zona, na hafta boshi, na oy nomi qatnashadi. Ya'ni uni qayta yozish hech narsa yutmaydi,
 * native maydonning klaviatura va ekran o'quvchisi bilan ishlashini esa yo'qotardi.
 */
export function DatePicker({
  mode = 'date',
  value,
  onChange,
  ariaLabel,
  name,
  className,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => readValue(mode, value), [mode, value]);

  // Panelda ko'rinib turgan oy. Qiymat bo'lsa o'shandan, bo'lmasa Toshkentdagi joriy oydan.
  const [cursor, setCursor] = useState<CivilMonth>(() => selectedMonth(selected) ?? currentMonth());
  const [view, setView] = useState<'days' | 'months'>(mode === 'month' ? 'months' : 'days');
  // Klaviatura fokusidagi kun — `roving tabindex` uchun: 42 ta katak 42 ta Tab to'xtashiga
  // aylanmasligi kerak, o'tish strelkalar bilan bo'ladi.
  const [focused, setFocused] = useState<CivilDate>(
    () => selected.date ?? { ...(selectedMonth(selected) ?? currentMonth()), day: 1 },
  );
  // Fokusni DOM da ko'chirish FAQAT klaviaturadan kelgan harakatdan keyin: sichqoncha bilan
  // oy almashtirilganda panel fokusni o'ziga tortib olmasligi kerak.
  const stealFocus = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => todayParts(), []);

  // Panel ochilishida ko'rinish qiymatga qaytariladi: oldingi safar boshqa oyga aylantirib
  // yopilgan panel keyingi ochilishda o'sha yerda turib qolmasin.
  useEffect(() => {
    if (!open) return;
    setCursor(selectedMonth(selected) ?? currentMonth());
    setView(mode === 'month' ? 'months' : 'days');
    setFocused(selected.date ?? { ...(selectedMonth(selected) ?? currentMonth()), day: 1 });
  }, [open, mode, selected]);

  useEffect(() => {
    if (!stealFocus.current || view !== 'days') return;
    stealFocus.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>('[data-focused="true"]')?.focus();
  }, [focused, view]);

  const commitDate = useCallback(
    (date: CivilDate) => {
      if (mode === 'datetime') {
        // Soat allaqachon tanlangan bo'lsa saqlanadi; bo'lmasa 00:00 — operator uni panelning
        // o'zida darhol to'g'rilaydi, panel esa ochiq qoladi.
        onChange(toDateTimeValue(date, selected.hour ?? 0, selected.minute ?? 0));
        return;
      }
      onChange(toDayKey(date));
      setOpen(false);
    },
    [mode, onChange, selected.hour, selected.minute],
  );

  const moveFocus = (delta: number) => {
    stealFocus.current = true;
    const next = shiftDay(focused, delta);
    setFocused(next);
    setCursor({ year: next.year, month: next.month });
  };

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      PageUp: -28,
      PageDown: 28,
    };
    const delta = step[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    moveFocus(delta);
  };

  const label = describeValue(mode, selected);

  return (
    <div className={cn('relative flex w-full min-w-0 items-center', className)}>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'border-input flex h-9 w-full min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none',
            'dark:bg-input/30 disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            // Tozalash tugmasi ustiga chiqadi — matn uning tagidan o'tib ketmasin.
            value === '' ? '' : 'pr-8',
          )}
        >
          <CalendarIcon className="text-muted-foreground size-3.5 shrink-0" aria-hidden="true" />
          <span className={cn('truncate', value === '' && 'text-muted-foreground')}>
            {label === '' ? strings.datePicker.placeholder : label}
          </span>
        </PopoverTrigger>

        <PopoverContent className="w-[17.5rem]">
          {view === 'days' ? (
            <>
              <PanelHeader
                onPrevious={() => setCursor(addMonths(cursor, -1))}
                onNext={() => setCursor(addMonths(cursor, 1))}
                previousLabel={strings.datePicker.previousMonth}
                nextLabel={strings.datePicker.nextMonth}
              >
                {/*
                  Sarlavha — "umurtqa": oy va yil alohida tugma. Ikkalasi ham oy ko'rinishiga
                  o'tkazadi, ya'ni boshqa oyga sakrash uchun chevronni o'n marta bosish shart
                  emas. Bu aynan shu paneldan kutiladigan yurish: eksport oralig'i ham, oylik
                  efirning `monthKey` i ham ko'pincha boshqa oyda.
                */}
                <button
                  type="button"
                  onClick={() => setView('months')}
                  className="hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring rounded-md px-2 py-1 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
                >
                  {formatMonthLong(cursor)}
                </button>
              </PanelHeader>

              {/*
                To'liq `grid` tuzilishi: sarlavha qatori ham, har bir hafta ham alohida `row`.
                Qatorlarsiz `gridcell` lar ekran o'quvchisi uchun yaroqsiz daraxt hosil qiladi —
                "shu ustunda nechanchi kun" degan savol javobsiz qolardi.
              */}
              <div
                className="mt-2 flex flex-col gap-0.5"
                role="grid"
                aria-label={ariaLabel}
                ref={gridRef}
                onKeyDown={onGridKeyDown}
              >
                <div role="row" className="grid grid-cols-7">
                  {WEEKDAY_NAMES.map((weekday) => (
                    <span
                      key={weekday}
                      role="columnheader"
                      className="text-muted-foreground pb-1 text-center text-[0.6875rem] font-medium"
                    >
                      {weekday}
                    </span>
                  ))}
                </div>

                {weeksOf(cursor).map((week) => (
                  <div
                    role="row"
                    key={toDayKey(week[0] as CalendarCell)}
                    className="grid grid-cols-7"
                  >
                    {week.map((cell) => {
                      const isSelected = selected.date !== null && isSameDay(cell, selected.date);
                      const isToday = isSameDay(cell, today);
                      const isFocused = isSameDay(cell, focused);

                      return (
                        <button
                          key={toDayKey(cell)}
                          type="button"
                          role="gridcell"
                          data-focused={isFocused}
                          aria-label={formatDayLong(cell)}
                          aria-selected={isSelected}
                          aria-current={isToday ? 'date' : undefined}
                          tabIndex={isFocused ? 0 : -1}
                          onClick={() => {
                            setFocused(cell);
                            commitDate(cell);
                          }}
                          className={cn(
                            'tabular relative mx-auto flex size-8 items-center justify-center rounded-md text-sm transition-colors',
                            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                            // Qo'shni oy kunlari YASHIRILMAYDI — hafta butun ko'rinsin — lekin so'niq.
                            cell.inMonth ? '' : 'text-muted-foreground/50',
                            isSelected
                              ? 'bg-primary text-primary-foreground font-medium'
                              : 'hover:bg-accent hover:text-accent-foreground',
                            /*
                              "Bugun" — TAGIGA CHIZIQ, to'ldirilgan doira emas.
                              To'ldirilgan anor rangi bitta narsani anglatadi: TANLANGAN. Agar
                              bugun ham to'ldirilgan bo'lsa, ikkala belgi bir-biriga o'xshab
                              qoladi va operator panelni noto'g'ri o'qiydi.
                            */
                            isToday && !isSelected
                              ? 'after:bg-primary after:absolute after:bottom-1 after:h-0.5 after:w-3.5 after:rounded-full'
                              : '',
                          )}
                        >
                          {cell.day}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <PanelHeader
                onPrevious={() => setCursor(addMonths(cursor, -12))}
                onNext={() => setCursor(addMonths(cursor, 12))}
                previousLabel={strings.datePicker.previousYear}
                nextLabel={strings.datePicker.nextYear}
              >
                <span className="tabular px-2 py-1 text-sm font-medium">{cursor.year}</span>
              </PanelHeader>

              <div className="mt-2 grid grid-cols-3 gap-1">
                {MONTH_NAMES.map((monthName, index) => {
                  const month = { year: cursor.year, month: index + 1 };
                  const isSelected =
                    selected.month !== null &&
                    selected.month.year === month.year &&
                    selected.month.month === month.month;
                  const isCurrent = today.year === month.year && today.month === month.month;

                  return (
                    <button
                      key={monthName}
                      type="button"
                      aria-label={formatMonthLong(month)}
                      aria-selected={isSelected}
                      aria-current={isCurrent ? 'date' : undefined}
                      onClick={() => {
                        setCursor(month);
                        if (mode === 'month') {
                          onChange(toMonthKey(month));
                          setOpen(false);
                          return;
                        }
                        // Kun rejimida oy tanlash — bu hali javob emas, faqat yo'l: panel
                        // o'sha oyning kunlariga o'tadi.
                        setFocused({ ...month, day: 1 });
                        setView('days');
                      }}
                      className={cn(
                        'relative flex h-9 items-center justify-center rounded-md text-sm transition-colors',
                        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                        isSelected
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'hover:bg-accent hover:text-accent-foreground',
                        isCurrent && !isSelected
                          ? 'after:bg-primary after:absolute after:bottom-1.5 after:h-0.5 after:w-3.5 after:rounded-full'
                          : '',
                      )}
                    >
                      {monthName.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {mode === 'datetime' ? (
            <label className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
              <span className="text-muted-foreground text-xs">{strings.datePicker.time}</span>
              <input
                type="time"
                aria-label={strings.datePicker.time}
                value={
                  selected.date === null
                    ? ''
                    : `${String(selected.hour ?? 0).padStart(2, '0')}:${String(selected.minute ?? 0).padStart(2, '0')}`
                }
                // Sana tanlanmaguncha soat ma'nosiz: `--:--` ni ISO ga aylantirib bo'lmaydi.
                disabled={selected.date === null}
                onChange={(event) => {
                  const match = /^(\d{2}):(\d{2})$/.exec(event.target.value);
                  if (!match || selected.date === null) return;
                  onChange(toDateTimeValue(selected.date, Number(match[1]), Number(match[2])));
                }}
                className="border-input tabular h-8 rounded-md border bg-transparent px-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
              />
            </label>
          ) : null}

          {/*
            Panelning pastki satri — "nima yuboriladi", mono shriftda.
            Jadvallardagi identifikatorlar bilan bir xil mantiq: operator bu qiymatni
            qo'llab-quvvatlash so'rovidagi sana bilan ustma-ust qo'yib solishtiradi.
          */}
          <div className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
            <span className="text-muted-foreground font-mono text-xs">
              {value === '' ? strings.datePicker.nothingSelected : value}
            </span>
            <button
              type="button"
              onClick={() => {
                if (mode === 'month') {
                  onChange(toMonthKey(today));
                  setOpen(false);
                  return;
                }
                setCursor({ year: today.year, month: today.month });
                setFocused(today);
                setView('days');
                commitDate(today);
              }}
              className="hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring rounded-md px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:outline-none"
            >
              {strings.datePicker.today}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/*
        Tozalash — ixtiyoriy filtrlar uchun SHART.
        Matn maydonida qiymatni o'chirish uchun uni belgilab `Delete` bosish yetardi; tugmada
        esa boshqa yo'l yo'q, ya'ni busiz moderatsiya filtrini qo'yib bo'lgach qaytarib
        bo'lmasdi.
      */}
      {value === '' || disabled ? null : (
        <button
          type="button"
          aria-label={`${ariaLabel} — ${strings.datePicker.clear}`}
          onClick={() => onChange('')}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute right-1.5 rounded-md p-1 focus-visible:ring-2 focus-visible:outline-none"
        >
          <XIcon className="size-3.5" aria-hidden="true" />
        </button>
      )}

      {name === undefined ? null : <input type="hidden" name={name} value={value} />}
    </div>
  );
}

function PanelHeader({
  children,
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
}: {
  children: React.ReactNode;
  onPrevious: () => void;
  onNext: () => void;
  previousLabel: string;
  nextLabel: string;
}) {
  const button =
    'hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex size-7 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none';

  return (
    <div className="flex items-center justify-between">
      <button type="button" aria-label={previousLabel} onClick={onPrevious} className={button}>
        <ChevronLeftIcon className="size-4" aria-hidden="true" />
      </button>
      {children}
      <button type="button" aria-label={nextLabel} onClick={onNext} className={button}>
        <ChevronRightIcon className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

interface ParsedValue {
  date: CivilDate | null;
  month: CivilMonth | null;
  hour: number | null;
  minute: number | null;
}

/** Satrni rejimga qarab o'qiydi. Yaroqsiz qiymat — tanlanmagan bilan bir xil ko'riladi. */
function readValue(mode: DatePickerMode, value: string): ParsedValue {
  if (mode === 'month') {
    const month = parseMonthKey(value);
    return { date: null, month, hour: null, minute: null };
  }

  if (mode === 'datetime') {
    const parsed = parseDateTimeValue(value);
    if (!parsed) return { date: null, month: null, hour: null, minute: null };
    return {
      date: parsed.date,
      month: { year: parsed.date.year, month: parsed.date.month },
      hour: parsed.hour,
      minute: parsed.minute,
    };
  }

  const date = parseDayKey(value);
  return {
    date,
    month: date === null ? null : { year: date.year, month: date.month },
    hour: null,
    minute: null,
  };
}

function selectedMonth(selected: ParsedValue): CivilMonth | null {
  return selected.month;
}

/** Tugmadagi matn: to'liq o'zbekcha sana, `2026-08-09` emas — u pastki satrda turadi. */
function describeValue(mode: DatePickerMode, selected: ParsedValue): string {
  if (mode === 'month') {
    return selected.month === null ? '' : formatMonthLong(selected.month);
  }
  if (selected.date === null) return '';

  const day = formatDayLong(selected.date);
  if (mode !== 'datetime') return day;

  const hour = String(selected.hour ?? 0).padStart(2, '0');
  const minute = String(selected.minute ?? 0).padStart(2, '0');
  return `${day}, ${hour}:${minute}`;
}

/** Oyning 42 katagini oltita haftaga bo'ladi — har biri o'z `row` i bo'ladi. */
function weeksOf(month: CivilMonth): CalendarCell[][] {
  const cells = monthGrid(month);
  const weeks: CalendarCell[][] = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }
  return weeks;
}

/** Kunni `delta` ga suradi — fuqarolik arifmetikasi, zona qatnashmaydi. */
function shiftDay({ year, month, day }: CivilDate, delta: number): CivilDate {
  const shifted = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}
